import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Eye } from "lucide-react";

var API = "/api";
var STORAGE_KEY = "rgp_recently_viewed";
var MAX_RECENT = 10;

// ---------- Recently-viewed local storage helpers ----------
export function getRecentlyViewed() {
  try {
    var raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    var arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}

export function pushRecentlyViewed(productId) {
  if (!productId) return;
  try {
    var current = getRecentlyViewed().filter(function (id) { return id !== productId; });
    current.unshift(productId);
    if (current.length > MAX_RECENT) current = current.slice(0, MAX_RECENT);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch (e) { /* private mode — ignore */ }
}

// ---------- Mini product card (compact) ----------
function MiniCard({ product, testIdPrefix }) {
  var price = product.sale_price && product.sale_price > 0 ? product.sale_price : product.price;
  var img = product.images && product.images.length > 0 ? product.images[0] : null;
  return (
    <Link
      to={"/product/" + product.id}
      className="group block bg-[#121212] border border-white/5 rounded-sm overflow-hidden hover:border-[#C0A062]/40 transition-colors"
      data-testid={testIdPrefix + "-" + product.id}
    >
      <div className="aspect-square bg-[#0a0a0a] overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#71717a] text-xs font-mono uppercase">No Image</div>
        )}
      </div>
      <div className="p-3">
        <p className="text-white text-sm font-medium line-clamp-2 group-hover:text-[#C0A062] transition-colors">{product.name}</p>
        <p className="text-[#C0A062] font-heading font-bold mt-1">${(price || 0).toFixed(2)}</p>
      </div>
    </Link>
  );
}

// ---------- Related Parts (same category, different SKU) ----------
function RelatedParts({ currentProduct }) {
  var [items, setItems] = useState([]);

  useEffect(function () {
    if (!currentProduct || !currentProduct.category) return;
    axios.get(API + "/products", { params: { category: currentProduct.category } })
      .then(function (res) {
        var pool = (res.data || []).filter(function (p) {
          return p.id !== currentProduct.id && p.in_stock !== false;
        });
        // Shuffle lightly so we don't always show the same 4
        for (var i = pool.length - 1; i > 0; i--) {
          var j = Math.floor(Math.random() * (i + 1));
          var tmp = pool[i]; pool[i] = pool[j]; pool[j] = tmp;
        }
        setItems(pool.slice(0, 4));
      })
      .catch(function () { setItems([]); });
  }, [currentProduct]);

  if (items.length === 0) return null;

  return (
    <section className="mt-16" data-testid="related-parts-section">
      <h2 className="font-heading font-bold text-white text-xl uppercase tracking-wide mb-6">Related Parts</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map(function (p) { return <MiniCard key={p.id} product={p} testIdPrefix="related-product" />; })}
      </div>
    </section>
  );
}

// ---------- Recently Viewed (localStorage-driven) ----------
function RecentlyViewed({ currentProductId }) {
  var [items, setItems] = useState([]);

  useEffect(function () {
    var ids = getRecentlyViewed().filter(function (id) { return id !== currentProductId; }).slice(0, 5);
    if (ids.length === 0) { setItems([]); return; }
    // Fetch all products once, then filter to the recent IDs in stored order
    axios.get(API + "/products")
      .then(function (res) {
        var byId = {};
        (res.data || []).forEach(function (p) { byId[p.id] = p; });
        var ordered = ids.map(function (id) { return byId[id]; }).filter(Boolean);
        setItems(ordered);
      })
      .catch(function () { setItems([]); });
  }, [currentProductId]);

  if (items.length === 0) return null;

  return (
    <section className="mt-12" data-testid="recently-viewed-section">
      <h2 className="font-heading font-bold text-white text-xl uppercase tracking-wide mb-6 flex items-center gap-2">
        <Eye className="w-5 h-5 text-[#C0A062]" /> Recently Viewed
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {items.map(function (p) { return <MiniCard key={p.id} product={p} testIdPrefix="recent-product" />; })}
      </div>
    </section>
  );
}

// ---------- Composed bottom block for ProductDetail ----------
export default function ProductDetailFooter({ product }) {
  return (
    <div className="container-tactical mt-8">
      <RelatedParts currentProduct={product} />
      <RecentlyViewed currentProductId={product ? product.id : null} />
    </div>
  );
}
